import { CeuUnit } from './ceu-unit';
import { ExternalCommunication, ExternalCommunicationWithConfig } from './external-communication';
import { CommunicationType, FuUnit } from './fu-unit';
import { Slot, SlotOptionType, Terminal } from './slot';

export class FcuUnit {
    id: string;
    vesselId: string;
    location: string;
    index: number;
}

export type NetworkEndpoint = {
    LineAIp: string;
    LineBIp: string;
    fieldNetLineAIp?: string;
    fieldNetLineBIp?: string;
    ceuCommLineAIp?: string;
    ceuCommLineBIp?: string;
};

export type MulticastEndpoint = {
    LineAIp: string;
    LineBIp: string;
};

export type FcuUnitPayload = {
    id?: string;
    vesselId?: string;
    location: string;
    index: number;
    network: {
        A: NetworkEndpoint;
        B: NetworkEndpoint;
        Multicasting: MulticastEndpoint;
    };
};

export type FcuUnitFull = {
    fcuUnit: FcuUnitPayload;
    fuUnitList: FuUnit[];
    slotList: Slot[];
    extComm: ExternalCommunication[];
    ceuUnitList: CeuUnit[];
};

export const fcuUnitMax = 8;

export enum FcuUnitAliases {
    fuUnits = 'fuUnits',
    slots = 'slots',
    extComms = 'extComms',
    ceuUnits = 'ceuUnits',
}

const fuUnitAlias = [FcuUnitAliases.fuUnits] as const;
const slotAlias = [FcuUnitAliases.slots] as const;
const extCommAlias = [FcuUnitAliases.extComms] as const;
const ceuUnitAlias = [FcuUnitAliases.ceuUnits] as const;

export type FcuUnitNormalizer = {
    selectedFcuUnit: FcuUnit;
    location: string;
    network: {
        fcuALineAIp: string;
        fcuALineBIp: string;
        fcuAFieldNetLineAIp: string;
        fcuAFieldNetLineBIp: string;
        fcuBLineAIp: string;
        fcuBLineBIp: string;
        fcuBFieldNetLineAIp: string;
        fcuBFieldNetLineBIp: string;
    };
    fuList: {
        id: string;
        index: number;
        location: string;
        communicationType: CommunicationType;
        fcuId: string;
        vesselId: string;
        fieldNetLineAIp: string;
        fieldNetLineBIp: string;
        slotList: {
            id: string;
            fcuId: string;
            fuId: string;
            vesselId: string;
            ioType: SlotOptionType;
            no: number;
            terminal: Terminal[];
            enable: boolean;
        }[];
    }[];
    slotList: {
        id: string;
        fcuId: string;
        fuId: string;
        vesselId: string;
        ioType: SlotOptionType;
        no: number;
        terminal: Terminal[];
        enable: boolean;
    }[];
    externalCommunication: ExternalCommunicationWithConfig;
    ceuList: { id: string; index: number; location: string }[];
};

export interface FcuExternalCommunications {
    fcuIndex: number;
    fcuId: string;
    extComms: ExternalCommunication;
    ceuUnits: CeuUnit[];
}

export const INCLUDE_ALIASES = {
    fuUnit: fuUnitAlias,
    slot: slotAlias,
    extComm: extCommAlias,
    ceuUnit: ceuUnitAlias,
} as const satisfies Record<string, readonly string[]>;

export type IncludeFlags = Record<string, boolean>;

export function parseInclude(include: string | string[] | undefined, aliasMap: Record<string, string[]>): IncludeFlags {
    const toKey = (s: string) => s.trim().toLowerCase();
    const tokens = Array.isArray(include)
        ? include.flatMap(x => String(x).split(','))
        : String(include ?? '').split(',');

    const set = new Set(tokens.map(toKey).filter(Boolean));

    const flags: IncludeFlags = {};
    for (const [flag, aliases] of Object.entries(aliasMap)) {
        flags[flag] = aliases.some(a => set.has(toKey(a)));
    }
    return flags;
}

export function getDefaultFcuData(fcuIndex: number, vesselId: string) {
    const fcuUnit: FcuUnitPayload = {
        vesselId: vesselId,
        location: '',
        index: fcuIndex,
        network: {
            A: {
                LineAIp: `192.168.60.10${fcuIndex}`,
                LineBIp: `192.168.61.10${fcuIndex}`,
                fieldNetLineAIp: '192.168.0.1',
                fieldNetLineBIp: '192.168.1.1',
                ceuCommLineAIp: '192.168.0.2',
                ceuCommLineBIp: '192.168.1.2',
            },
            B: {
                LineAIp: `192.168.60.12${fcuIndex}`,
                LineBIp: `192.168.61.12${fcuIndex}`,
                fieldNetLineAIp: '192.168.0.21',
                fieldNetLineBIp: '192.168.1.21',
                ceuCommLineAIp: '192.168.0.22',
                ceuCommLineBIp: '192.168.1.22',
            },
            Multicasting: { LineAIp: '239.255.255.250', LineBIp: '239.255.255.250' },
        },
    };

    return fcuUnit;
}
